/**
 * The browser's own install offer and what this device remembers about installing the panel.
 *
 * Chromium browsers (Chrome, Edge, Samsung Internet, Opera, …) fire `beforeinstallprompt` once per
 * page load, as soon as the panel can be installed and is not installed yet; keeping the event
 * lets the panel install with one click instead of the browser's own mini-bar. It can fire before
 * the signed-in layout exists, so index.tsx starts listening before React renders.
 */
import {isInNativeApp} from './androidApp'

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{outcome: 'accepted' | 'dismissed'; platform?: string}>
}

export type InstallPromptState = {
  /** The browser offered to install and the offer has not been used yet. */
  canPrompt: boolean
  /** The browser reported the panel installed during this page's life. */
  installedNow: boolean
}

let deferred: BeforeInstallPromptEvent | null = null
let state: InstallPromptState = {canPrompt: false, installedNow: false}
let started = false
const listeners = new Set<() => void>()

const setState = (next: InstallPromptState) => {
  state = next
  listeners.forEach((listener) => listener())
}

export const startInstallPromptCapture = () => {
  if (started || typeof window === 'undefined') return
  started = true
  window.addEventListener('beforeinstallprompt', (event) => {
    // The panel shows its own offer (InstallAppGate) instead of the browser's mini-bar.
    event.preventDefault()
    deferred = event as BeforeInstallPromptEvent
    setState({...state, canPrompt: true})
  })
  window.addEventListener('appinstalled', () => {
    deferred = null
    rememberInstalled()
    setState({canPrompt: false, installedNow: true})
  })
}

export const subscribeInstallPrompt = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const getInstallPromptState = () => state

/** Opens the browser's install dialog; an offer is good for one dialog only. */
export const promptInstall = async (): Promise<InstallOutcome> => {
  const event = deferred
  if (!event) return 'unavailable'
  deferred = null
  setState({...state, canPrompt: false})
  await event.prompt()
  const choice = await event.userChoice
  return choice.outcome
}

/** Whether this browser makes install offers at all (Chromium); the others need their menu. */
export const browserOffersInstall = () =>
  typeof window !== 'undefined' && 'onbeforeinstallprompt' in window

/** The panel runs as the installed app (its own window or home-screen app), not in a browser tab. */
export const isRunningInstalled = () => {
  if (typeof window === 'undefined') return false
  const standalone = (navigator as Navigator & {standalone?: boolean}).standalone === true
  const displayMode =
    typeof window.matchMedia === 'function' &&
    ['standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay'].some(
      (mode) => window.matchMedia(`(display-mode: ${mode})`).matches
    )
  // The POS devices' Android app shows the panel in its web view and offers `Android` (AppClearCache);
  // «معتمد آسا» for Android marks its user agent and offers window.asaApp (androidApp.ts).
  const posApp = typeof (window as Window & {Android?: unknown}).Android === 'object'
  return (
    standalone ||
    displayMode ||
    posApp ||
    isInNativeApp() ||
    document.referrer.startsWith('android-app://')
  )
}

/**
 * Asks Chromium whether the panel is installed on this device although it is open in a tab
 * (manifest related_applications, platform webapp). Unknown wherever the question is not offered.
 */
export const checkInstalledElsewhere = async (): Promise<boolean | undefined> => {
  const nav = navigator as Navigator & {
    getInstalledRelatedApps?: () => Promise<{platform: string}[]>
  }
  if (typeof nav.getInstalledRelatedApps !== 'function') return undefined
  try {
    const apps = await nav.getInstalledRelatedApps()
    return apps.some((app) => app.platform === 'webapp')
  } catch {
    return undefined
  }
}

// ------------------------------------------------------------------ what this device remembers

/** Installing is per device and browser, so the choice lives in this browser, not on the server. */
export const INSTALL_STORAGE_KEY = 'asa:install-app:v1'
export const SNOOZE_DAYS = 14
/** After an install the offer stays away this long, even if the app is removed later. */
export const INSTALLED_QUIET_DAYS = 180
const DAY_MS = 24 * 60 * 60 * 1000

type Remembered = {snoozedUntil?: number; installedAt?: number}

const readRemembered = (): Remembered => {
  try {
    const raw = window.localStorage.getItem(INSTALL_STORAGE_KEY)
    const value = raw ? JSON.parse(raw) : null
    return value && typeof value === 'object' ? value : {}
  } catch {
    return {}
  }
}

const writeRemembered = (value: Remembered) => {
  try {
    window.localStorage.setItem(INSTALL_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Without storage the offer may come back on the next visit, nothing worse.
  }
}

export const snoozeInstallOffer = (now = Date.now()) =>
  writeRemembered({...readRemembered(), snoozedUntil: now + SNOOZE_DAYS * DAY_MS})

export const rememberInstalled = (now = Date.now()) =>
  writeRemembered({...readRemembered(), installedAt: now})

/** The panel was installed from this browser lately (appinstalled). */
export const wasInstalledHere = (now = Date.now()) => {
  const installedAt = Number(readRemembered().installedAt)
  return installedAt > 0 && now - installedAt < INSTALLED_QUIET_DAYS * DAY_MS
}

/** The dashboard's offer waits: «بعداً» was chosen lately. */
export const isInstallOfferSnoozed = (now = Date.now()) =>
  Number(readRemembered().snoozedUntil) > now

// ------------------------------------------------------------------ opening the guide

/** «نصب برنامه» (account menu, «امکانات تازه», dashboard) asks the guide in the layout to open. */
export const OPEN_INSTALL_GUIDE_EVENT = 'mp:install-app'

export const openInstallGuide = (): void => {
  window.dispatchEvent(new Event(OPEN_INSTALL_GUIDE_EVENT))
}
